import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import Loader from "../../components/Loader";
import Gnavbar from "../../components/Gnavbar";
import {
  deleteRequest,
  getRequest,
  postWithFormData,
  putWithFormData,
} from "../../helpers/api.helper";
import { toast } from "react-toastify";
import { UPLOAD_URL } from "../../utils/constants";

type TestimonialType = {
  id: number;
  name: string;
  message: string;
  image: string;
  status: string;
  created_at: string;
};

const TestimonialsManagement = () => {
  const [testimonials, setTestimonials] = useState<TestimonialType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] =
    useState<TestimonialType | null>(null);
  const [deleteTestimonial, setDeleteTestimonial] =
    useState<TestimonialType | null>(null);

  // ✅ Form states
  const [formData, setFormData] = useState({
    name: "",
    message: "",
    status: "active",
  });
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`/bureau/testimonials`);
      if (response.success) {
        setTestimonials(response.data || []);
      } else {
        toast.error(response.message || "Error fetching testimonials");
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      toast.error("Error fetching testimonials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTestimonial(null);
    setFormData({
      name: "",
      message: "",
      status: "active",
    });
    setImage(null);
    setPreview(null);
    setIsModalOpen(true);
  };

  const openEditModal = (testimonial: TestimonialType) => {
    setEditingTestimonial(testimonial);
    setFormData({
      name: testimonial.name,
      message: testimonial.message,
      status: testimonial.status,
    });
    const previewUrl = testimonial.image
      ? `${UPLOAD_URL}/bureaus/testimonials/${testimonial.image}`
      : null;


    setPreview(previewUrl);
    setIsModalOpen(true);
  };

  const openDeleteModal = (testimonial: TestimonialType) => {
    setDeleteTestimonial(testimonial);
    setIsDeleteModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTestimonial(null);
    setImage(null);
    setPreview(null);
    setFormData({
      name: "",
      message: "",
      status: "active",
    });
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteTestimonial(null);
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  // ✅ Handle create / update
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("message", formData.message);
      formDataToSend.append("status", formData.status);
      if (image) formDataToSend.append("image", image);
      let response;
      if (editingTestimonial) {
        response = await putWithFormData(
          `/bureau/testimonials/${editingTestimonial.id}`,
          formDataToSend
        );
      } else {
        response = await postWithFormData(
          `/bureau/testimonials`,
          formDataToSend
        );
      }
      if (response.success) {
        toast.success(
          editingTestimonial
            ? "Testimonial updated successfully!"
            : "Testimonial created successfully!"
        );
        closeModal();
        fetchTestimonials();
      } else {
        toast.error(response.data.message || "Error saving testimonial");
      }
    } catch (error) {
      console.error("Error saving testimonial:", error);
      toast.error("Error saving testimonial. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Handle delete
  const handleDelete = async () => {
    if (!deleteTestimonial) return;
    setSubmitting(true);

    try {
      const response = await deleteRequest(
        `/bureau/testimonials/${deleteTestimonial?.id}`,
        {}
      );
      if (response.success) {
        toast.success("Testimonial deleted successfully!");
        closeDeleteModal();
        fetchTestimonials();
      } else {
        toast.error(response.data.message || "Error deleting testimonial");
      }
    } catch (error) {
      console.error("Error deleting testimonial:", error);
      toast.error("Error deleting testimonial. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Format Date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Gnavbar />
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <button
              onClick={openCreateModal}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <FaPlus className="mr-2" />
              Add New Testimonial
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : testimonials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className={`bg-white rounded-lg shadow-md overflow-hidden ${
                    testimonial.status === "inactive" ? "opacity-60" : ""
                  }`}
                >
                  {testimonial.image && (
                    <img
                      src={
                        UPLOAD_URL +
                        "/bureaus/testimonials/" +
                        testimonial.image
                      }
                      alt={testimonial.name}
                      className="w-full h-40 object-cover"
                    />
                  )}
                  <div className="p-4 border-t">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {testimonial.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      "{testimonial.message}"
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                      {formatDate(testimonial.created_at)}
                    </p>

                    <div className="flex justify-between items-center">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(testimonial)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                        >
                          <FaEdit size={16} />
                        </button>
                        <button
                          onClick={() => openDeleteModal(testimonial)}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          testimonial.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {testimonial.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FaUser size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No testimonials yet.</p>
            </div>
          )}
        </div>

        {/* ✅ Modal for Add/Edit */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {editingTestimonial
                      ? "Edit Testimonial"
                      : "Add Testimonial"}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes size={20} />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      placeholder="Enter client name"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      placeholder="Enter testimonial message"
                      required
                    />
                  </div>

                  {/* ✅ Image Upload */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Image (optional)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full"
                    />
                    {preview && (
                      <img
                        src={preview}
                        alt="Preview"
                        className="mt-3 w-32 h-32 object-cover rounded-lg border"
                      />
                    )}
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 py-3 border rounded-lg text-gray-700 hover:bg-gray-50"
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      disabled={submitting}
                    >
                      {submitting
                        ? editingTestimonial
                          ? "Updating..."
                          : "Creating..."
                        : editingTestimonial
                        ? "Update Testimonial"
                        : "Create Testimonial"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-red-600 mb-4">
                Delete Testimonial
              </h2>
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete testimonial from{" "}
                <strong>{deleteTestimonial?.name}</strong>?
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={closeDeleteModal}
                  className="flex-1 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {submitting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestimonialsManagement;
